import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img, { ImgProps } from "@insite/mobius/Img";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    imageUrl = "imageUrl",
    altText = "altText",
    heading = "heading",
    description = "description",
    imgLink = "imgLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.heading]: string;
        [fields.imageUrl]: string;
        [fields.altText]: string;
        [fields.description]: string;
        [fields.imgLink]: LinkFieldValue;
    };
}
type Props = OwnProps;

export interface GAIAHCPStarterDispensarystyles {
    dispensaryWrapper?: InjectableCss;
    dispensaryImgContainer?: LinkProps;
    dispensarydescriptionWrapper?: InjectableCss;
    dispensaryTitle?: TypographyProps;
    img?: ImgProps;
}
export const gaiaHCPStarterDispensarystyles: GAIAHCPStarterDispensarystyles = {
    dispensaryWrapper: {
        css: css`
            padding: 16px 14px;
            background: #f3efeb;
            border-radius: 4px;
            margin-top: 25px;
        `,
    },
    dispensaryImgContainer: {
        css: css`
            margin-bottom: 12px;
            display: block;
        `,
    },
    img: {
        css: css`
            width: 100%;
            height: 100%;
        `,
    },

    dispensaryTitle: {
        variant: "h4",
        fontFamily: "LatoBold",
        css: css`
            font-size: 1rem;
            margin-bottom: 13px;
            text-transform: capitalize;
        `,
    },
    dispensarydescriptionWrapper: {
        css: css`
            p,
            span,
            a {
                font-size: 0.875rem;
            }
        `,
    },
};
const styles = gaiaHCPStarterDispensarystyles;
const GaiaHCPStarterDispensary: React.FC<Props> = props => {
    const { url } = useGetLink(props.fields.imgLink);
    return (
        <>
            <StyledWrapper {...styles.dispensaryWrapper}>
                <Typography {...styles.dispensaryTitle}>{props.fields.heading}</Typography>
                <Link
                    {...styles.dispensaryImgContainer}
                    href={url || "#"}
                    target="_blank"
                    aria-label="clickable image to redirect on Gaia Herbs Professional Solutions Starter Dispensary chart Pdf"
                >
                    <Img src={props.fields.imageUrl} altText={props.fields.altText} loading="lazy" {...styles.img} />
                </Link>
                <StyledWrapper {...styles.dispensarydescriptionWrapper}>
                    {parse(props.fields.description, parserOptions)}
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const definition: WidgetDefinition = {
    group: "Basic",
    displayName: "HCP Starter Dispensary",
    fieldDefinitions: [
        {
            name: fields.heading,
            displayName: "heading",
            editorTemplate: "TextField",
            defaultValue: "Starter Dispensary",
            fieldType: "General",
            tooltip: "text tip",
            isRequired: true,
        },
        {
            name: fields.imageUrl,
            fieldType: "General",
            editorTemplate: "ImagePickerField",
            defaultValue: "",
        },
        {
            name: fields.altText,
            fieldType: "General",
            editorTemplate: "TextField",
            defaultValue: "",
        },
        {
            name: fields.imgLink,
            fieldType: "General",
            editorTemplate: "LinkField",
            defaultValue: { type: "Page", value: "" },
        },
        {
            name: fields.description,
            displayName: "Description",
            editorTemplate: "RichTextField",
            defaultValue: "",
            fieldType: "Translatable",
        },
    ],
};
const widgetModule: WidgetModule = {
    component: GaiaHCPStarterDispensary,
    definition,
};

export default widgetModule;
